import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';

const ACCENT = '#2e9f86';
const inp = { border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
const btn = (bg, color) => ({ background: bg, color, border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' });

const blank = () => ({ name: '', sku: '', barcode: '', price_usd: '', cost: '', cost_currency: 'USD', stock: '', attributes: [{ name: '', value: '' }] });

export default function VariantList() {
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => api.get('/api/settings').then(r => r.data) });
  const rate    = settings?.exchange_rate || 89500;
  const showLbp = settings?.currency?.show_lbp ?? true;
  const secCode = settings?.currency?.secondary_code || 'LBP';

  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => api.get('/api/categories').then(r => r.data) });

  const { data: allProducts = [], isLoading } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get('/api/products', { params: { all: true } }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
  });
  const variables = allProducts.filter(p => p.type === 'variable');

  // ── product panel ──────────────────────────────────────────────────────────
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [pName,     setPName]     = useState('');
  const [pCatId,    setPCatId]    = useState('');
  const [pError,    setPError]    = useState('');

  const saveProductMutation = useMutation({
    mutationFn: ({ name, categoryId, editingId }) => {
      const payload = { name, category_id: categoryId || null, type: 'variable' };
      return editingId ? api.put(`/api/products/${editingId}`, payload) : api.post('/api/products', payload);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries(['products-all']);
      queryClient.invalidateQueries(['products']);
      const id = res.data?._id || editing?._id;
      setPanelOpen(false);
      if (id) setExpandedId(id); // auto-open variants for new product
    },
    onError: (err) => setPError(err.response?.data?.error || 'Error saving'),
  });

  const deleteProductMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/products/${id}`),
    onSuccess: () => { queryClient.invalidateQueries(['products-all']); queryClient.invalidateQueries(['products']); },
  });

  const openNew  = () => { setEditing(null); setPName(''); setPCatId(''); setPError(''); setPanelOpen(true); };
  const openEdit = (p) => { setEditing(p); setPName(p.name); setPCatId(p.category_id ? String(p.category_id) : ''); setPError(''); setPanelOpen(true); };
  const closePanel = () => { setPanelOpen(false); setEditing(null); };

  // ── variants panel ─────────────────────────────────────────────────────────
  const [expandedId,    setExpandedId]    = useState(null);
  const [editingVariant,setEditingVariant]= useState(null);
  const [vForm,         setVForm]         = useState(blank());
  const [vError,        setVError]        = useState('');

  const { data: variants = [] } = useQuery({
    queryKey: ['variants', expandedId],
    queryFn: () => api.get(`/api/products/${expandedId}/variants`).then(r => r.data),
    enabled: !!expandedId,
  });

  const saveVariantMutation = useMutation({
    mutationFn: (data) => editingVariant
      ? api.put(`/api/variants/${editingVariant._id}`, data)
      : api.post(`/api/products/${expandedId}/variants`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['variants', expandedId]);
      setEditingVariant(null); setVForm(blank()); setVError('');
    },
    onError: (err) => setVError(err.response?.data?.error || 'Error'),
  });

  const deleteVariantMutation = useMutation({
    mutationFn: (vid) => api.delete(`/api/variants/${vid}`),
    onSuccess: () => queryClient.invalidateQueries(['variants', expandedId]),
  });

  const handleVSubmit = (e) => {
    e.preventDefault(); setVError('');
    if (!vForm.name.trim()) { setVError('Name is required'); return; }
    saveVariantMutation.mutate({
      ...vForm,
      price_usd: Number(vForm.price_usd) || 0,
      price_lbp: Math.round(Number(vForm.price_usd) * rate) || 0,
      cost: Number(vForm.cost) || 0,
      stock: Number(vForm.stock) || 0,
      attributes: vForm.attributes.filter(a => a.name && a.value),
    });
  };

  const updateAttr = (i, field, val) => {
    const attrs = [...vForm.attributes]; attrs[i][field] = val;
    setVForm({ ...vForm, attributes: attrs });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>{variables.length} variable product{variables.length !== 1 ? 's' : ''}</div>
        <button onClick={openNew} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ New Variable Product</button>
      </div>

      {isLoading ? <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading…</div>
        : variables.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 60 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎛️</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>No variable products yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Variable products have multiple variants (e.g. sizes, colors)</div>
          </div>
        ) : variables.map(p => {
          const cat = categories.find(c => String(c._id) === String(p.category_id));
          const isOpen = expandedId === p._id;
          return (
            <div key={p._id} style={{ background: '#fff', border: `1.5px solid ${isOpen ? ACCENT : '#e5e7eb'}`, borderRadius: 12, marginBottom: 12, overflow: 'hidden', transition: 'border-color 0.15s' }}>
              {/* product row */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 }}>
                <button onClick={() => setExpandedId(isOpen ? null : p._id)} style={{ background: isOpen ? '#f0fdf8' : '#f3f4f6', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 14, color: isOpen ? ACCENT : '#6b7280', flexShrink: 0 }}>
                  {isOpen ? '▾' : '▸'}
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>🎛️ {p.name}</div>
                  {cat && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>📂 {cat.name}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => openEdit(p)} style={btn('#eff6ff', '#2563eb')}>Edit</button>
                  <button onClick={() => { if (window.confirm(`Delete "${p.name}" and all its variants?`)) deleteProductMutation.mutate(p._id); }} style={btn('#fee2e2', '#b91c1c')}>Delete</button>
                </div>
              </div>

              {/* variants expanded */}
              {isOpen && (
                <div style={{ borderTop: '1.5px solid #f0f0f0', padding: '16px 16px 20px' }}>
                  {/* variant form */}
                  <form onSubmit={handleVSubmit} style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, border: '1.5px solid #e5e7eb', marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: ACCENT, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {editingVariant ? 'Edit Variant' : 'Add Variant'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {/* name */}
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Name *</label>
                        <input value={vForm.name} onChange={e => setVForm({ ...vForm, name: e.target.value })} style={inp} placeholder="e.g. Large Red" />
                      </div>
                      {/* price */}
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Price (USD)</label>
                        <input type="number" min="0" step="0.01" value={vForm.price_usd} onChange={e => setVForm({ ...vForm, price_usd: e.target.value })} style={{ ...inp, borderColor: ACCENT, background: '#f0fdf8' }} placeholder="0.00" />
                      </div>
                      {/* cost */}
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Cost</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input type="number" min="0" step="0.01" value={vForm.cost} onChange={e => setVForm({ ...vForm, cost: e.target.value })} style={{ ...inp, flex: 1 }} placeholder="0" />
                          <select value={vForm.cost_currency} onChange={e => setVForm({ ...vForm, cost_currency: e.target.value })} style={{ ...inp, width: 70, flex: 'none' }}>
                            <option value="USD">USD</option>
                            {showLbp && <option value={secCode}>{secCode}</option>}
                          </select>
                        </div>
                      </div>
                      {/* sku */}
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>SKU</label>
                        <input value={vForm.sku} onChange={e => setVForm({ ...vForm, sku: e.target.value })} style={inp} />
                      </div>
                      {/* barcode */}
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Barcode</label>
                        <input value={vForm.barcode} onChange={e => setVForm({ ...vForm, barcode: e.target.value })} style={inp} />
                      </div>
                      {/* stock */}
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Stock</label>
                        <input type="number" min="0" value={vForm.stock} onChange={e => setVForm({ ...vForm, stock: e.target.value })} style={{ ...inp, width: '50%' }} placeholder="0" />
                      </div>
                      {/* attributes */}
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Attributes</label>
                        {vForm.attributes.map((attr, i) => (
                          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                            <input placeholder="Name (e.g. Size)" value={attr.name} onChange={e => updateAttr(i, 'name', e.target.value)} style={{ ...inp, flex: 1 }} />
                            <input placeholder="Value (e.g. L)" value={attr.value} onChange={e => updateAttr(i, 'value', e.target.value)} style={{ ...inp, flex: 1 }} />
                            <button type="button" onClick={() => setVForm({ ...vForm, attributes: vForm.attributes.filter((_, idx) => idx !== i) })} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 700, fontSize: 16, padding: '0 4px' }}>✕</button>
                          </div>
                        ))}
                        <button type="button" onClick={() => setVForm({ ...vForm, attributes: [...vForm.attributes, { name: '', value: '' }] })} style={{ background: 'none', border: 'none', color: ACCENT, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>+ Add Attribute</button>
                      </div>
                    </div>
                    {vError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{vError}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button type="submit" disabled={saveVariantMutation.isPending} style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saveVariantMutation.isPending ? 0.6 : 1 }}>
                        {saveVariantMutation.isPending ? 'Saving…' : editingVariant ? 'Update Variant' : 'Add Variant'}
                      </button>
                      {editingVariant && (
                        <button type="button" onClick={() => { setEditingVariant(null); setVForm(blank()); setVError(''); }} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                      )}
                    </div>
                  </form>

                  {/* variant list */}
                  {variants.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 12 }}>No variants yet — add one above</div>
                  ) : variants.map(v => (
                    <div key={v._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 8, marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{v.name}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>${Number(v.price_usd || 0).toFixed(2)}</span>
                          {v.cost ? <span style={{ fontSize: 11, color: '#9ca3af' }}>Cost: {Number(v.cost).toFixed(2)} {v.cost_currency}</span> : null}
                          <span style={{ fontSize: 11, color: '#6b7280' }}>· Stock: {v.stock || 0}</span>
                          {v.sku ? <span style={{ fontSize: 11, color: '#6b7280' }}>· SKU: {v.sku}</span> : null}
                        </div>
                        {v.attributes?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                            {v.attributes.map((a, i) => (
                              <span key={i} style={{ background: '#ede9fe', color: '#5b21b6', fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>{a.name}: {a.value}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 10 }}>
                        <button onClick={() => {
                          setEditingVariant(v);
                          setVForm({ name: v.name, sku: v.sku || '', barcode: v.barcode || '', price_usd: v.price_usd || '', cost: v.cost || '', cost_currency: v.cost_currency || 'USD', stock: v.stock || '', attributes: v.attributes?.length ? v.attributes : [{ name: '', value: '' }] });
                          setVError('');
                        }} style={btn('#eff6ff', '#2563eb')}>Edit</button>
                        <button onClick={() => { if (window.confirm('Delete this variant?')) deleteVariantMutation.mutate(v._id); }} style={btn('#fee2e2', '#b91c1c')}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

      {/* overlay */}
      {panelOpen && <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40, backdropFilter: 'blur(2px)' }} />}

      {/* product panel */}
      {panelOpen && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 420, height: '100%', background: '#fff', zIndex: 50, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1.5px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{editing ? 'Edit Variable Product' : 'New Variable Product'}</div>
            <button onClick={closePanel} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', fontSize: 18, lineHeight: 1, color: '#6b7280' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
            {pError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{pError}</div>}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Name *</label>
              <input value={pName} onChange={e => setPName(e.target.value)} style={inp} placeholder="e.g. T-Shirt" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Category</label>
              <select value={pCatId} onChange={e => setPCatId(e.target.value)} style={{ ...inp, background: '#fafafa' }}>
                <option value="">— No category —</option>
                {categories.map(c => <option key={c._id} value={String(c._id)}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', background: '#f0fdf8', borderRadius: 8, padding: '10px 12px' }}>
              💡 After saving, the product opens and you can add variants (sizes, colors, etc.) directly.
            </div>
          </div>
          <div style={{ padding: '14px 20px', borderTop: '1.5px solid #f0f0f0', display: 'flex', gap: 10 }}>
            <button onClick={closePanel} style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => { if (!pName.trim()) { setPError('Name is required'); return; } saveProductMutation.mutate({ name: pName.trim(), categoryId: pCatId, editingId: editing?._id }); }}
              disabled={saveProductMutation.isPending}
              style={{ flex: 2, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: saveProductMutation.isPending ? 0.7 : 1 }}>
              {saveProductMutation.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create & Add Variants →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
